/* eslint-disable no-unused-vars, eqeqeq, react-hooks/exhaustive-deps */
import { useEffect, useState, Fragment, useRef } from "react";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import {
  getRooms,
  getDataLogs,
  exportDataLogsCsv,
} from "../service/service_handler";

const COL_WIDTH_KEY = "data_logs_col_widths_v1";
const MIN_COL_WIDTH = 100;

const dataLogColumns = [
  { key: "logDate", label: "Log Date", align: "left", defaultWidth: 120 },
  { key: "roomName", label: "Room Name", align: "right", defaultWidth: 150 },
  { key: "sensorName", label: "Sensor Name", align: "right", defaultWidth: 160 },
  {
    key: "dliTotal",
    label: "DLI (Total) (mol/m2/d)",
    align: "right",
    defaultWidth: 140,
  },
  {
    key: "ppfd",
    label: "PPFD (μmol/m2/s)",
    align: "left",
    defaultWidth: 280,
  },
  { key: "lux", label: "Lux (lx)", align: "left", defaultWidth: 240 },
  {
    key: "roomTemperature",
    label: "Room Temperature (°C)",
    align: "left",
    defaultWidth: 240,
  },
  {
    key: "roomHumidity",
    label: "Room Humidity (%)",
    align: "left",
    defaultWidth: 220,
  },
];

function getInitialWidths(storageKey, columns) {
  const defaults = {};
  columns.forEach((col) => {
    defaults[col.key] = col.defaultWidth;
  });
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return defaults;
    const parsed = JSON.parse(saved);
    if (!parsed || typeof parsed !== "object") return defaults;
    columns.forEach((col) => {
      const value = Number(parsed[col.key]);
      if (!Number.isNaN(value) && value >= MIN_COL_WIDTH) {
        defaults[col.key] = value;
      }
    });
    return defaults;
  } catch (err) {
    return defaults;
  }
}

function getColumnBodySx(width) {
  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    whiteSpace: "normal",
    wordBreak: "break-word",
    verticalAlign: "top",
  };
}

function getColumnHeadSx(width) {
  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    position: "relative",
    whiteSpace: "normal",
    wordBreak: "break-word",
    verticalAlign: "top",
  };
}

export default function DataLogs() {
  const [roomNames, setRoomNames] = useState([]);
  const [mainRoomName, setMainRoomName] = useState("");
  const [logs, setLogs] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [openSnackErrMsg, setopenSnackErrMsg] = useState("");
  const [noRoomData, setNoRoomData] = useState(false);
  const [noData, setNoData] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [colWidths, setColWidths] = useState(() =>
    getInitialWidths(COL_WIDTH_KEY, dataLogColumns)
  );
  const resizeRef = useRef({
    columnKey: "",
    startX: 0,
    startWidth: 0,
  });

  const showError = (error) => {
    if (!error.response) {
      setopenSnackErrMsg("Network error please try again");
    } else {
      setopenSnackErrMsg(
        (error.response.data && error.response.data.code) ||
          "Service error - Please try again later"
      );
    }
    setOpenSnack(true);
  };

  const fetchLogs = (roomId, fromDateValue, toDateValue) => {
    const params = {};
    if (roomId) {
      params.roomId = roomId;
    }
    if (fromDateValue) {
      params.fromDate = fromDateValue;
    }
    if (toDateValue) {
      params.toDate = toDateValue;
    }
    getDataLogs(params)
      .then((response) => {
        setLogs(response.data.logs || []);
        setNoData((response.data.logs || []).length === 0);
      })
      .catch((error) => {
        showError(error);
        setNoData(true);
      });
  };

  useEffect(() => {
    getRooms()
      .then((response) => {
        const rooms = response.data.rooms || [];
        setRoomNames(rooms);
        if (rooms.length > 0) {
          setMainRoomName(rooms[0]);
          fetchLogs(rooms[0].roomId, fromDate, toDate);
        } else {
          setNoRoomData(true);
          fetchLogs(undefined, fromDate, toDate);
        }
      })
      .catch((error) => {
        showError(error);
        setNoRoomData(true);
        fetchLogs(undefined, fromDate, toDate);
      });
  }, []);

  useEffect(() => {
    localStorage.setItem(COL_WIDTH_KEY, JSON.stringify(colWidths));
  }, [colWidths]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const { columnKey, startX, startWidth } = resizeRef.current;
      if (!columnKey) return;
      const delta = event.clientX - startX;
      const nextWidth = Math.max(MIN_COL_WIDTH, startWidth + delta);
      setColWidths((prev) => ({ ...prev, [columnKey]: nextWidth }));
    };

    const handleMouseUp = () => {
      resizeRef.current = {
        columnKey: "",
        startX: 0,
        startWidth: 0,
      };
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const startResize = (columnKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      columnKey,
      startX: event.clientX,
      startWidth: Number(colWidths[columnKey]) || MIN_COL_WIDTH,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleMainRoomChange = (event) => {
    const selectedRoom = event.target.value;
    setMainRoomName(selectedRoom);
    fetchLogs(selectedRoom.roomId, fromDate, toDate);
  };

  const handleApplyFilters = () => {
    if (fromDate && toDate && fromDate > toDate) {
      setopenSnackErrMsg("From Date cannot be greater than To Date");
      setOpenSnack(true);
      return;
    }
    const roomId = mainRoomName?.roomId;
    fetchLogs(roomId, fromDate, toDate);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const handleExportCsv = () => {
    const params = {};
    if (mainRoomName && mainRoomName.roomId) {
      params.roomId = mainRoomName.roomId;
    }
    if (fromDate) {
      params.fromDate = fromDate;
    }
    if (toDate) {
      params.toDate = toDate;
    }
    exportDataLogsCsv(params)
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const roomName = mainRoomName?.roomName
          ? mainRoomName.roomName.replace(/\s+/g, "_")
          : "all_rooms";
        link.download = `data_logs_${roomName}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        showError(error);
      });
  };

  const snackAction = (
    <Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleCloseSnack}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Fragment>
  );

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography
        sx={{
          mb: 1.5,
          fontWeight: "bold",
          color: "text.primary",
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
        }}
        variant="h5"
        component="div"
      >
        Data Logs
      </Typography>

      <Snackbar
        open={openSnack}
        autoHideDuration={2500}
        onClose={handleCloseSnack}
        message={openSnackErrMsg}
        action={snackAction}
      />

      <Stack
        direction="row"
        spacing={2}
        mb={3}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ width: "100%" }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="data-logs-room-select-label">Choose Room</InputLabel>
            <Select
              labelId="data-logs-room-select-label"
              id="data-logs-room-select"
              value={mainRoomName}
              label="Choose Room"
              onChange={handleMainRoomChange}
            >
              {noRoomData ? (
                <MenuItem value="">No Data Found</MenuItem>
              ) : (
                roomNames.map((room) => (
                  <MenuItem key={room.roomId} value={room}>
                    {room.roomName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" onClick={handleApplyFilters}>
            Apply
          </Button>
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            ml: { xs: 0, sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-end", sm: "flex-start" },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            00:00-24:00 Logs : Recorded every 30 Mins. 60 Days Retention
          </Typography>
          <Button variant="contained" onClick={handleExportCsv}>
            <DownloadIcon sx={{ mr: 0.8 }} />
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table
          sx={{
            width: "max-content",
            minWidth: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              py: { xs: 0.75, sm: 1 },
              px: { xs: 0.75, sm: 1.25 },
              minWidth: 0,
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontWeight: 600,
              bgcolor: "action.hover",
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              height: "auto",
              minHeight: "48px",
            },
          }}
          aria-label="data logs table"
          size="small"
        >
          <TableHead>
            <TableRow>
              {dataLogColumns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={getColumnHeadSx(colWidths[col.key])}
                >
                  {col.label}
                  <Box
                    onMouseDown={(event) => startResize(col.key, event)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: -4,
                      width: 8,
                      height: "100%",
                      cursor: "col-resize",
                      zIndex: 2,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "18%",
                        bottom: "18%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "1px",
                        bgcolor: "divider",
                      },
                      "&:hover": { bgcolor: "action.selected" },
                      "&:hover::before": {
                        width: "2px",
                        bgcolor: "primary.main",
                      },
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((row) => (
              <TableRow
                key={row.sensorDailyLogId}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  align="left"
                  sx={getColumnBodySx(colWidths.logDate)}
                >
                  {row.logDate}
                </TableCell>
                <TableCell
                  align="right"
                  sx={getColumnBodySx(colWidths.roomName)}
                >
                  {row.roomName}
                </TableCell>
                <TableCell
                  align="right"
                  sx={getColumnBodySx(colWidths.sensorName)}
                >
                  {row.sensorName}
                </TableCell>
                <TableCell
                  align="right"
                  sx={getColumnBodySx(colWidths.dliTotal)}
                >
                  {row.dliTotal}
                </TableCell>
                <TableCell
                  align="left"
                  sx={getColumnBodySx(colWidths.ppfd)}
                >
                  {row.ppfd}
                </TableCell>
                <TableCell align="left" sx={getColumnBodySx(colWidths.lux)}>
                  {row.lux}
                </TableCell>
                <TableCell
                  align="left"
                  sx={getColumnBodySx(colWidths.roomTemperature)}
                >
                  {row.roomTemperature}
                </TableCell>
                <TableCell
                  align="left"
                  sx={getColumnBodySx(colWidths.roomHumidity)}
                >
                  {row.roomHumidity}
                </TableCell>
              </TableRow>
            ))}
            {noData && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No data logs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
